import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from as fromPromise, Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { JsonDecoder } from 'ts.data.json';
import { ENVIRONMENT, IEnvironment } from '@trade-platform/ui-utils';
import {
    profileDecoder,
    pylonIdentityDecoder,
    resetMFADecoder,
    sharedExternalFirmsDecoder
} from './decoder';
import { Profile, PylonIdentity, SharedExternalFirm, UpdateProfilePayload } from './model';

@Injectable()
export class AixUsersService {
    private http = inject(HttpClient);
    private environment = inject<IEnvironment>(ENVIRONMENT);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    getAdvisorLogged(): Observable<Profile> {
        return this.http
            .get<Profile>(`${this.environment.api}me`)
            .pipe(concatMap(p => fromPromise(profileDecoder.decodeToPromise(p))));
    }

    updateAdvisorLogged(payload: UpdateProfilePayload): Observable<Profile> {
        return this.http
            .put<Profile>(`${this.environment.api}me`, payload)
            .pipe(concatMap(p => fromPromise(profileDecoder.decodeToPromise(p))));
    }

    resetPassword() {
        return this.http
            .get<boolean>(`${this.environment.api}me/password_reset`)
            .pipe(concatMap(p => fromPromise(JsonDecoder.boolean.decodeToPromise(p))));
    }

    resetMFA() {
        return this.http
            .delete<null>(`${this.environment.api}me/mfa`)
            .pipe(concatMap((resp: null) => fromPromise(resetMFADecoder.decodeToPromise(resp))));
    }

    /**
     * Pylon support-chat config + the server-computed identity hash for the logged-in user.
     *
     * advisors-api answers 503 when Pylon is not configured for the environment, and gates the route
     * on the `pylonChat` tenant feature, so callers must treat any failure as "no chat available"
     * rather than falling back to an unverified identity.
     */
    getPylonIdentity(): Observable<PylonIdentity> {
        return this.http
            .get<PylonIdentity>(`${this.environment.api}me/pylon_identity`)
            .pipe(concatMap(p => fromPromise(pylonIdentityDecoder.decodeToPromise(p))));
    }

    getSharedFirms(): Observable<SharedExternalFirm[]> {
        return this.http
            .get<any>(`${this.environment.api}users/accessGroups/listByUserWithSharing`)
            .pipe(concatMap(p => fromPromise(sharedExternalFirmsDecoder.decodeToPromise(p))));
    }
}
